// ============================================================================
// ChatMessage — renders a single user or assistant bubble plus any attached
// proposal cards. The cards live in flow beneath the assistant text, not
// inside the bubble — they need full width.
// ============================================================================

import { Link } from 'react-router-dom';
import type { CopilotMessage, CopilotProposalBase } from '../../shared/types';
import { AddSubcontractorCard } from './cards/add-subcontractor-card';
import { TaskCard } from './cards/task-card';
import { SupplyOrderCard } from './cards/supply-order-card';
import { CloseTaskCard } from './cards/close-task-card';
import { HiringRequestCard } from './cards/hiring-request-card';
import { SubReplacementCard } from './cards/sub-replacement-card';
import { SupplyParUpdateCard } from './cards/supply-par-update-card';
import { ProjectWorkCard } from './cards/project-work-card';
import { StockMovementCard } from './cards/stock-movement-card';
import { BoardActionCard } from './cards/board-action-card';
import { BuildingActionCard } from './cards/building-action-card';
import { ProjectActionCard } from './cards/project-action-card';
import { NewAccountActionCard } from './cards/new-account-action-card';
import { TaskActionCard } from './cards/task-action-card';
import { MyWorkActionCard } from './cards/my-work-action-card';
import { SubActionCard } from './cards/sub-action-card';
import { HiringActionCard } from './cards/hiring-action-card';
import { OnboardingActionCard } from './cards/onboarding-action-card';
import { SupplyActionCard } from './cards/supply-action-card';
import { MachineActionCard } from './cards/machine-action-card';
import { SeparationItemCard } from './cards/separation-item-card';
import { MileageCard } from './cards/mileage-card';
import { TimeActionCard } from './cards/time-action-card';

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

// ── What a reply may show (Ask AI phone basics, 2026-10-21) ─────────────────
// A safe subset of markdown, built as React elements (never HTML strings):
// **bold**, *italic*, bullet lists ("- item"), numbered lists ("1. item"), and links —
// [Open X](/path?with=query) as an in-app link, https:// as an outside link.
// Everything else shows as the words it is. No new dependency: the old help
// chat's renderer built an HTML string for dangerouslySetInnerHTML and had no
// lists or links, so it is not reused.
const INLINE_RX = /\[([^\]]{1,120})\]\(((?:\/|https:\/\/)[^)\s]*)\)|\*\*([^*\n]{1,300})\*\*|(?<![*\w])\*([^*\s][^*\n]{0,298}[^*\s]|[^*\s])\*(?![*\w])/g;

function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RX)) {
    const idx = m.index ?? 0;
    if (idx > last) nodes.push(text.slice(last, idx));
    const key = `${keyBase}-${i++}`;
    if (m[3] !== undefined) {
      nodes.push(<strong key={key}>{m[3]}</strong>);
    } else if (m[4] !== undefined) {
      nodes.push(<em key={key}>{m[4]}</em>);
    } else if (m[2].startsWith('/')) {
      nodes.push(
        <Link
          key={key}
          to={m[2]}
          className="mx-0.5 my-0.5 inline-flex min-h-[44px] items-center gap-1 rounded-full bg-tcs-inverse px-3 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-soft"
        >
          {m[1]} →
        </Link>,
      );
    } else {
      nodes.push(
        <a
          key={key}
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-[44px] items-center underline"
        >
          {m[1]}
        </a>,
      );
    }
    last = idx + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const BULLET_RX = /^\s*(?:[-*•])\s+(.*)$/;
const NUMBERED_RX = /^\s*\d{1,2}[.)]\s+(.*)$/;

export function renderAssistantText(content: string): React.ReactNode {
  if (!content) return null;
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length === 0) return;
    const k = `p${blocks.length}`;
    blocks.push(
      <p key={k} className="whitespace-pre-wrap">
        {para.flatMap((l, n) => (n === 0 ? renderInline(l, `${k}-${n}`) : ['\n', ...renderInline(l, `${k}-${n}`)]))}
      </p>,
    );
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    const k = `l${blocks.length}`;
    const items = list.items.map((it, n) => <li key={`${k}-${n}`}>{renderInline(it, `${k}-${n}`)}</li>);
    blocks.push(
      list.ordered ? (
        <ol key={k} className="ml-5 list-decimal space-y-1">{items}</ol>
      ) : (
        <ul key={k} className="ml-5 list-disc space-y-1">{items}</ul>
      ),
    );
    list = null;
  };
  for (const line of lines) {
    const b = line.match(BULLET_RX);
    const n = b ? null : line.match(NUMBERED_RX);
    if (b || n) {
      flushPara();
      const ordered = !!n;
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push((b ?? n)![1]);
    } else if (line.trim() === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return <div className="space-y-2">{blocks}</div>;
}

interface ChatMessageProps {
  message: CopilotMessage;
  facilities: FacilityOpt[];
  committingId: string | null;
  onCommit: (
    proposal: CopilotProposalBase,
    edited_input: Record<string, unknown>,
    was_edited: boolean,
  ) => void;
  onDiscard: (proposal: CopilotProposalBase) => void;
}

export function ChatMessage({
  message,
  facilities,
  committingId,
  onCommit,
  onDiscard,
}: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className="w-full">
      <div
        className={`flex w-full ${
          isUser ? 'justify-end' : 'justify-start'
        }`}
      >
        <div
          className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${isUser ? 'whitespace-pre-wrap ' : ''}${
            isUser
              ? 'bg-tcs-inverse text-tcs-inverse-ink'
              : 'bg-tcs-surface text-tcs-text'
          }`}
        >
          {isUser
            ? message.content || ''
            : renderAssistantText(message.content) || '…'}
        </div>
      </div>

      {!isUser && message.proposals && message.proposals.length > 0 && (
        <div className="mt-3 space-y-3">
          {message.proposals.map((p: CopilotProposalBase) => {
            if (p.tool_name === 'propose_task') {
              return (
                <TaskCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_supply_order') {
              return (
                <SupplyOrderCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_close_task') {
              return (
                <CloseTaskCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_hiring_request') {
              return (
                <HiringRequestCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_sub_replacement') {
              return (
                <SubReplacementCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_supply_par_update') {
              return (
                <SupplyParUpdateCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_project_work') {
              return (
                <ProjectWorkCard
                  key={p.action_id}
                  proposal={p}
                  facilities={facilities}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_stock_movement') {
              return (
                <StockMovementCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_board_action') {
              return (
                <BoardActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_time_action') {
              return (
                <TimeActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_mileage') {
              return (
                <MileageCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_separation_item') {
              return (
                <SeparationItemCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_machine_action') {
              return (
                <MachineActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_supply_action') {
              return (
                <SupplyActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_onboarding_action') {
              return (
                <OnboardingActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_hiring_action') {
              return (
                <HiringActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_sub_action') {
              return (
                <SubActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_my_work_action') {
              return (
                <MyWorkActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_task_action') {
              return (
                <TaskActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_new_account_action') {
              return (
                <NewAccountActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_project_action') {
              return (
                <ProjectActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_building_action') {
              return (
                <BuildingActionCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            if (p.tool_name === 'propose_add_subcontractor') {
              return (
                <AddSubcontractorCard
                  key={p.action_id}
                  proposal={p}
                  isCommitting={committingId === p.action_id}
                  onDoIt={(input, was_edited) => onCommit(p, input, was_edited)}
                  onDiscard={() => onDiscard(p)}
                />
              );
            }
            // A draft from a building tool Ask AI no longer has (batch 3 moved
            // them all into one): say so, and link the building, instead of
            // showing nothing or offering a save that no longer exists.
            return <RetiredDraft key={p.action_id} proposal={p} />;
          })}
        </div>
      )}
    </div>
  );
}

/** An earlier draft whose tool was retired: shown, never saved from here. */
function RetiredDraft({ proposal }: { proposal: CopilotProposalBase }) {
  const fid = typeof proposal.input?.facility_id === 'string' ? proposal.input.facility_id : null;
  const done = proposal.status === 'Committed' || proposal.status === 'Edited_And_Committed';
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text-secondary">
      {done ? 'An earlier card here was saved.' : proposal.status === 'Discarded' ? 'An earlier card here was discarded.' : 'An earlier draft here can no longer be saved from Ask AI — ask again, or do it on the building.'}
      {fid && (
        <Link to={`/account-management/facilities/${fid}`} className="ml-1 inline-flex min-h-[44px] items-center underline">
          Open the building
        </Link>
      )}
    </div>
  );
}
